import { isAvailableBecauseCanEditFile } from '@/app/actions';
import { Action } from '@/app/actions/actions';
import type { ActionSpecRecord } from '@/app/actions/actionsSpec';
import { gridToDataTable } from '@/app/actions/dataTableSpec';
import { setMobilePane } from '@/app/atoms/mobileStateAtom';
import { events } from '@/app/events/events';
import { preventCodeCellInTable } from '@/app/grid/actions/openCodeEditor';
import { sheets } from '@/app/grid/controller/Sheets';
import { FILE_INPUT_ID, IMAGE_INPUT_ID } from '@/app/gridGL/HTMLGrid/GridFileInput';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { CodeCellLanguage, JsRenderCodeCell } from '@/app/quadratic-core-types';
import { insertCellRef } from '@/app/ui/menus/CodeEditor/insertCellRef';
import { pivotPanelTarget } from '@/app/ui/menus/Pivot/pivotPanelUtils';
import { SNIPPET_JS_API, SNIPPET_JS_CHART } from '@/app/ui/menus/CodeEditor/snippetsJS';
import { SNIPPET_PY_API, SNIPPET_PY_CHART } from '@/app/ui/menus/CodeEditor/snippetsPY';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import {
  ArrowDropDownIcon,
  CheckBoxIcon,
  CommentAddIcon,
  CommentIcon,
  DataValidationsIcon,
  DeleteIcon,
  EditIcon,
  FileCopyIcon,
  FormatDateTimeIcon,
  FormatPaintIcon,
  ImageIcon,
  InsertChartIcon,
  PivotTableIcon,
  InsertLinkIcon,
  LabelIcon,
  ScheduledTasksIcon,
  SheetIcon,
  TableConvertIcon,
  TableIcon,
} from '@/shared/components/Icons';

type InsertActionSpec = Pick<
  ActionSpecRecord,
  | Action.InsertCodePython
  | Action.InsertCodeJavascript
  | Action.InsertCodeFormula
  | Action.InsertChartPython
  | Action.InsertChartJavascript
  | Action.InsertChartNative
  | Action.ToggleChartPanel
  | Action.EditChart
  | Action.DuplicateChart
  | Action.DeleteChart
  | Action.InsertPivotTable
  | Action.EditPivotTable
  | Action.DeletePivotTable
  | Action.PivotShowDetails
  | Action.InsertApiRequestJavascript
  | Action.InsertApiRequestPython
  | Action.InsertDataTable
  | Action.InsertImage
  | Action.InsertImageUrl
  | Action.InsertSheet
  | Action.InsertCheckbox
  | Action.InsertDropdown
  | Action.ToggleDataValidation
  | Action.ToggleDefinedNames
  | Action.ToggleConditionalFormat
  | Action.AddConditionalFormat
  | Action.InsertCellComment
  | Action.ToggleComments
  | Action.InsertScheduledTask
  | Action.InsertHyperlink
  | Action.InsertCellReference
  | Action.RemoveInsertedCells
  | Action.InsertToday
  | Action.InsertTodayTime
  | Action.InsertFile
>;

export const insertCellReference = () => {
  const codeEditorState = gridSettings.getCodeEditorState();
  if (codeEditorState.showCodeEditor) {
    const { sheetId, language } = codeEditorState.codeCell;
    insertCellRef(sheetId, language);
  }
};

// Opens the code editor to create a new code cell at the cursor. Aborts (with a
// warning) when the cursor is inside a data table, since code/formulas are not
// supported in tables.
const insertCodeCell = (language: CodeCellLanguage, initialCode = '', isSingleCell = false) => {
  if (preventCodeCellInTable()) return;

  const cursor = sheets.sheet.cursor.position;
  gridSettings.updateCodeEditorState((prev) => ({
    ...prev,
    diffEditorContent: undefined,
    waitingForEditorClose: {
      codeCell: {
        sheetId: sheets.current,
        pos: { x: cursor.x, y: cursor.y },
        language,
        lastModified: 0,
        ...(isSingleCell ? { isSingleCell: true } : {}),
      },
      showCellTypeMenu: false,
      inlineEditor: false,
      initialCode,
    },
  }));
};

/// The pivot report the cursor is in, if any. Pivot actions operate on the
/// report under the cursor the way chart actions operate on the selected chart.
const getSelectedPivotTable = (): JsRenderCodeCell | undefined => {
  const cursor = sheets.sheet.cursor.position;
  const table = content.cellsSheet.tables.getCodeCellIntersects({ x: cursor.x, y: cursor.y });
  return table?.pivot ? table : undefined;
};

export const insertActionsSpec: InsertActionSpec = {
  [Action.InsertCodePython]: {
    label: () => 'Python',
    labelVerbose: 'Insert Python code',
    run: () => insertCodeCell('Python'),
  },
  [Action.InsertCodeJavascript]: {
    label: () => 'JavaScript',
    labelVerbose: 'Insert JavaScript code',
    run: () => insertCodeCell('Javascript'),
  },
  [Action.InsertCodeFormula]: {
    label: () => 'Formula',
    labelVerbose: 'Insert Formula',
    run: () => insertCodeCell('Formula', '', true),
  },
  [Action.InsertChartNative]: {
    label: () => 'Chart',
    labelVerbose: 'Insert chart',
    Icon: InsertChartIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showChartPanel: 'new',
      }));
    },
  },
  [Action.ToggleChartPanel]: {
    label: () => 'Manage charts',
    labelVerbose: 'Manage charts',
    Icon: InsertChartIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showChartPanel: true,
      }));
    },
  },
  [Action.EditChart]: {
    label: () => 'Edit chart',
    Icon: EditIcon,
    run: () => {
      const chart = content.chartsLayer.selected;
      if (!chart) return;
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showChartPanel: chart.id,
      }));
      if (isMobileMode()) {
        setMobilePane('charts');
      }
    },
  },
  [Action.DuplicateChart]: {
    label: () => 'Duplicate chart',
    Icon: FileCopyIcon,
    run: async () => {
      const chart = content.chartsLayer.selected;
      if (!chart) return;
      const newId = await quadraticCore.duplicateChart(chart.sheet_id, chart.id);
      if (newId) {
        content.chartsLayer.select(newId);
      }
    },
  },
  [Action.DeleteChart]: {
    label: () => 'Delete chart',
    Icon: DeleteIcon,
    run: () => {
      const chart = content.chartsLayer.selected;
      if (!chart) return;
      quadraticCore.removeChart(chart.sheet_id, chart.id, false);
      content.chartsLayer.select(undefined);
    },
  },
  [Action.InsertPivotTable]: {
    label: () => 'PivotTable',
    labelVerbose: 'Insert PivotTable',
    Icon: PivotTableIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showCreatePivotTable: true,
      }));
    },
  },
  [Action.EditPivotTable]: {
    label: () => 'PivotTable fields',
    labelVerbose: 'Show PivotTable fields',
    Icon: PivotTableIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: () => {
      const table = getSelectedPivotTable();
      if (!table) return;
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showPivotPanel: pivotPanelTarget(sheets.current, table.x, table.y),
      }));
    },
  },
  [Action.PivotShowDetails]: {
    label: () => 'Show details',
    labelVerbose: 'Show PivotTable details',
    Icon: TableIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: async () => {
      const table = getSelectedPivotTable();
      if (!table) return;
      const cursor = sheets.sheet.cursor.position;
      const result = await quadraticCore.pivotDrillToDetail(sheets.current, cursor.x, cursor.y);
      if (result.error) {
        gridSettings.addGlobalSnackbar?.(result.error, { severity: 'error' });
      } else if (result.sheetId) {
        sheets.current = result.sheetId;
      }
    },
  },
  [Action.DeletePivotTable]: {
    label: () => 'Delete PivotTable',
    Icon: DeleteIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: () => {
      const table = getSelectedPivotTable();
      if (!table) return;
      quadraticCore.deletePivotTable(sheets.current, table.x, table.y, false);
    },
  },
  [Action.InsertChartPython]: {
    label: () => 'Python (Plotly)',
    labelVerbose: 'Insert Python chart (Plotly)',
    run: () => insertCodeCell('Python', SNIPPET_PY_CHART),
  },
  [Action.InsertChartJavascript]: {
    label: () => 'JavaScript (Chart.js)',
    labelVerbose: 'Insert JavaScript chart (Chart.js)',
    run: () => insertCodeCell('Javascript', SNIPPET_JS_CHART),
  },
  [Action.InsertFile]: {
    label: () => 'From file (CSV, Excel, or Parquet)',
    labelVerbose: 'Insert file (CSV, Excel, or Parquet)',
    run: () => {
      const el = document.getElementById(FILE_INPUT_ID) as HTMLInputElement;
      if (el) {
        el.click();

        // clear the file input to trigger the onChange event for subsequent
        // file imports
        el.value = '';
      }
    },
  },
  [Action.InsertApiRequestJavascript]: {
    label: () => 'From JavaScript API request',
    labelVerbose: 'Insert JavaScript API request',
    run: () => insertCodeCell('Javascript', SNIPPET_JS_API),
  },
  [Action.InsertApiRequestPython]: {
    label: () => 'From Python API request',
    labelVerbose: 'Insert Python API request',
    run: () => insertCodeCell('Python', SNIPPET_PY_API),
  },
  [Action.InsertDataTable]: {
    label: () => 'Table',
    labelVerbose: 'Insert a table',
    Icon: TableConvertIcon,
    isDisabled: () => sheets.sheet.cursor.canConvertToDataTable(),
    run: () => gridToDataTable(),
  },
  [Action.InsertImage]: {
    label: () => 'Image',
    labelVerbose: 'Insert image from file',
    Icon: ImageIcon,
    run: () => {
      const el = document.getElementById(IMAGE_INPUT_ID) as HTMLInputElement;
      if (el) {
        el.click();
        el.value = '';
      }
    },
  },
  [Action.InsertImageUrl]: {
    label: () => 'Image from URL',
    labelVerbose: 'Insert image from URL',
    Icon: ImageIcon,
    run: () => {
      events.emit('insertImageUrl');
    },
  },
  [Action.InsertSheet]: {
    label: () => 'Sheet',
    labelVerbose: 'Insert Sheet',
    Icon: SheetIcon,
    run: () => {
      sheets.userAddSheet();
    },
  },
  [Action.InsertCheckbox]: {
    label: () => 'Checkbox',
    labelVerbose: 'Insert checkbox',
    Icon: CheckBoxIcon,
    run: () => {
      sheets.sheet.addCheckbox();
    },
  },
  [Action.InsertDropdown]: {
    label: () => 'Dropdown',
    labelVerbose: 'Insert dropdown',
    Icon: ArrowDropDownIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showValidation: 'list',
      }));
    },
  },
  [Action.ToggleDataValidation]: {
    label: () => 'Data validations',
    labelVerbose: 'Manage data validation rules',
    Icon: DataValidationsIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showValidation: true,
      }));
    },
  },
  [Action.ToggleDefinedNames]: {
    label: () => 'Named ranges',
    labelVerbose: 'Manage named cells and ranges',
    Icon: LabelIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showDefinedNames: true,
      }));
    },
  },
  [Action.ToggleConditionalFormat]: {
    label: () => 'Conditional formatting',
    labelVerbose: 'Manage conditional formatting rules',
    Icon: FormatPaintIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showConditionalFormat: true,
      }));
    },
  },
  [Action.AddConditionalFormat]: {
    label: () => 'Add conditional format',
    labelVerbose: 'Add conditional formatting rule',
    Icon: FormatPaintIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showConditionalFormat: 'new',
      }));
    },
  },
  [Action.InsertCellComment]: {
    label: () => 'Comment',
    labelVerbose: 'Insert comment',
    Icon: CommentAddIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: async () => {
      const { openCommentPopover } = await import('@/app/atoms/commentsAtom');
      const cursor = sheets.sheet.cursor.position;
      openCommentPopover({ x: cursor.x, y: cursor.y, focus: true });
    },
  },
  [Action.ToggleComments]: {
    label: () => 'Comments',
    labelVerbose: 'Show all comments',
    Icon: CommentIcon,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({
        ...prev,
        showComments: !prev.showComments,
      }));
    },
  },
  [Action.InsertScheduledTask]: {
    label: () => 'Scheduled task',
    labelVerbose: 'Insert scheduled task',
    Icon: ScheduledTasksIcon,
    run: async () => {
      const { showScheduledTasksDialog, CREATE_TASK_ID } = await import('@/jotai/scheduledTasksAtom');
      showScheduledTasksDialog(CREATE_TASK_ID);
    },
  },
  [Action.InsertHyperlink]: {
    label: () => 'Hyperlink',
    labelVerbose: 'Insert hyperlink',
    Icon: InsertLinkIcon,
    run: () => {
      events.emit('insertLink');
    },
  },
  [Action.InsertCellReference]: {
    label: () => 'Cell reference',
    labelVerbose: 'Insert cell reference',
    run: insertCellReference,
  },
  [Action.RemoveInsertedCells]: {
    label: () => 'Remove inserted cells',
    run: () => {}, // TODO(ayush): add this when refactoring shortcuts to use action specs
  },
  [Action.InsertToday]: {
    label: () => "Insert today's date",
    Icon: FormatDateTimeIcon,
    run: () => {
      const sheet = sheets.sheet;
      const cursor = sheet.cursor;
      const today = new Date();
      const formattedDate = `${today.getFullYear()}/${today.getMonth() + 1}/${today.getDate()}`;
      quadraticCore.setCellValue(sheet.id, cursor.position.x, cursor.position.y, formattedDate, false);
    },
  },
  [Action.InsertTodayTime]: {
    label: () => "Insert today's time",
    Icon: FormatDateTimeIcon,
    run: () => {
      const sheet = sheets.sheet;
      const cursor = sheet.cursor;
      const today = new Date();
      const formattedTime = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      quadraticCore.setCellValue(sheet.id, cursor.position.x, cursor.position.y, formattedTime, false);
    },
  },
};
